
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface SiteSettings {
  siteName: string;
  siteDescription: string;
  contactEmail: string;
  theme: string;
  compactMode: boolean;
  animations: boolean;
  lowStockThreshold: number;
  trackInventory: boolean;
  allowBackorders: boolean;
  defaultProductStatus: string;
  emailNotifications: boolean;
  lowStockAlerts: boolean;
  systemUpdates: boolean;
  autoBackup: boolean;
  maintenanceMode: boolean;
  logoUrl?: string;
  logoText?: string;
  showLogoIcon?: boolean;
  showLogoText?: boolean;
  logoFontFamily?: string;
  logoFontWeight?: number;
  // SEO Settings
  seoTitle?: string;
  seoDescription?: string;
  seoKeywords?: string;
  ogImageUrl?: string;
  canonicalUrl?: string;
  googleSiteVerification?: string;
  googleAnalyticsId?: string;
  // Business Information
  businessName?: string;
  businessDescription?: string;
  businessAddress?: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
  businessPhone?: string;
  businessHours?: {
    monday?: string;
    tuesday?: string;
    wednesday?: string;
    thursday?: string;
    friday?: string;
    saturday?: string;
    sunday?: string;
  };
  socialLinks?: {
    facebook?: string;
    twitter?: string;
    instagram?: string;
    linkedin?: string;
    youtube?: string;
  };
  // Schema Controls
  enableStructuredData?: boolean;
  enableProductSchema?: boolean;
  enableOrganizationSchema?: boolean;
  enableWebsiteSchema?: boolean;
  // Favicon and PWA Settings
  faviconUrl?: string;
  pwaIconUrl?: string;
  appleTouchIconUrl?: string;
  pwaManifest?: {
    name?: string;
    short_name?: string;
    description?: string;
    theme_color?: string;
    background_color?: string;
    display?: string;
    start_url?: string;
    scope?: string;
    icons?: Array<{
      src: string;
      sizes: string;
      type: string;
      purpose?: string;
    }>;
  };
}

export const defaultSettings: SiteSettings = {
    siteName: "VRAM",
    siteDescription: "Advanced admin management system",
    contactEmail: "admin@vram.com",
    theme: "dark",
    compactMode: false,
    animations: true,
    lowStockThreshold: 10,
    trackInventory: true,
    allowBackorders: false,
    defaultProductStatus: "draft",
    emailNotifications: true,
    lowStockAlerts: true,
    systemUpdates: true,
    autoBackup: false,
    maintenanceMode: false,
    logoUrl: "",
    logoText: "VRAM",
    showLogoIcon: true,
    showLogoText: true,
    logoFontFamily: "Poppins",
    logoFontWeight: 600,
    // SEO Defaults
    seoTitle: "VRAM Store - Quality Products Online",
    seoDescription: "Discover amazing products at VRAM Store. Quality guaranteed, fast shipping, competitive prices.",
    seoKeywords: "online store, quality products, fast shipping, competitive prices",
    ogImageUrl: "",
    canonicalUrl: "",
    googleSiteVerification: "",
    googleAnalyticsId: "",
    // Business Defaults
    businessName: "VRAM Store",
    businessDescription: "Your trusted online store for quality products",
    businessAddress: {
      street: "",
      city: "",
      state: "",
      zipCode: "",
      country: "Australia"
    },
    businessPhone: "",
    businessHours: {
      monday: "9:00 AM - 5:00 PM",
      tuesday: "9:00 AM - 5:00 PM",
      wednesday: "9:00 AM - 5:00 PM",
      thursday: "9:00 AM - 5:00 PM",
      friday: "9:00 AM - 5:00 PM",
      saturday: "10:00 AM - 4:00 PM",
      sunday: "Closed"
    },
    socialLinks: {
      facebook: "",
      twitter: "",
      instagram: "",
      linkedin: "",
      youtube: ""
    },
    // Schema Defaults
    enableStructuredData: true,
    enableProductSchema: true,
    enableOrganizationSchema: true,
    enableWebsiteSchema: true,
    // Favicon and PWA Defaults
    faviconUrl: "",
    pwaIconUrl: "",
    appleTouchIconUrl: "",
    pwaManifest: {
      name: "VRAM Store",
      short_name: "VRAM",
      description: "Advanced admin management system",
      theme_color: "#000000",
      background_color: "#ffffff",
      display: "standalone",
      start_url: "/",
      scope: "/",
      icons: []
    }
};

export const useSiteSettings = () => {
  return useQuery({
    queryKey: ['site-settings'],
    queryFn: async () => {
      try {
        // SiteSettings: Fetching public site configuration
        
        // Use a direct query with proper typing
        const response = await fetch(
          `https://yahtgibtatgcvbniropq.supabase.co/rest/v1/public_site_config?id=eq.main&select=*`,
          {
            headers: {
              'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InlhaHRnaWJ0YXRnY3Zibmlyb3BxIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDg0Mjg3MTAsImV4cCI6MjA2NDAwNDcxMH0.dRE9hVBRpepyvIcjB3Oyu7PCuc23mYa5OAUEearyIBY',
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.ok) {
          console.warn('SiteSettings: Failed to fetch public config:', response.statusText);
          return defaultSettings;
        }

        const data = await response.json();

        if (data && data.length > 0) {
          const row = data[0];
          // Map database columns to interface properties
          return {
            siteName: row.site_name || defaultSettings.siteName,
            siteDescription: row.site_description || defaultSettings.siteDescription,
            contactEmail: row.contact_email || defaultSettings.contactEmail,
            theme: row.theme || defaultSettings.theme,
            compactMode: defaultSettings.compactMode,
            animations: defaultSettings.animations,
            lowStockThreshold: defaultSettings.lowStockThreshold,
            trackInventory: defaultSettings.trackInventory,
            allowBackorders: defaultSettings.allowBackorders,
            defaultProductStatus: defaultSettings.defaultProductStatus,
            emailNotifications: defaultSettings.emailNotifications,
            lowStockAlerts: defaultSettings.lowStockAlerts,
            systemUpdates: defaultSettings.systemUpdates,
            autoBackup: defaultSettings.autoBackup,
            maintenanceMode: defaultSettings.maintenanceMode,
            logoUrl: row.logo_url || defaultSettings.logoUrl,
            logoText: row.logo_text || defaultSettings.logoText,
            showLogoIcon: row.show_logo_icon ?? defaultSettings.showLogoIcon,
            showLogoText: row.show_logo_text ?? defaultSettings.showLogoText,
            logoFontFamily: row.logo_font_family || defaultSettings.logoFontFamily,
            logoFontWeight: row.logo_font_weight || defaultSettings.logoFontWeight,
            // SEO Fields
            seoTitle: row.seo_title || defaultSettings.seoTitle,
            seoDescription: row.seo_description || defaultSettings.seoDescription,
            seoKeywords: row.seo_keywords || defaultSettings.seoKeywords,
            ogImageUrl: row.og_image_url || defaultSettings.ogImageUrl,
            canonicalUrl: row.canonical_url || defaultSettings.canonicalUrl,
            googleSiteVerification: row.google_site_verification || defaultSettings.googleSiteVerification,
            googleAnalyticsId: row.google_analytics_id || defaultSettings.googleAnalyticsId,
            // Business Fields
            businessName: row.business_name || defaultSettings.businessName,
            businessDescription: row.business_description || defaultSettings.businessDescription,
            businessAddress: row.business_address || defaultSettings.businessAddress,
            businessPhone: row.business_phone || defaultSettings.businessPhone,
            businessHours: row.business_hours || defaultSettings.businessHours,
            socialLinks: row.social_links || defaultSettings.socialLinks,
            // Schema Controls
            enableStructuredData: row.enable_structured_data ?? defaultSettings.enableStructuredData,
            enableProductSchema: row.enable_product_schema ?? defaultSettings.enableProductSchema,
            enableOrganizationSchema: row.enable_organization_schema ?? defaultSettings.enableOrganizationSchema,
            enableWebsiteSchema: row.enable_website_schema ?? defaultSettings.enableWebsiteSchema,
            // Favicon and PWA Fields
            faviconUrl: row.favicon_url || defaultSettings.faviconUrl,
            pwaIconUrl: row.pwa_icon_url || defaultSettings.pwaIconUrl,
            appleTouchIconUrl: row.apple_touch_icon_url || defaultSettings.appleTouchIconUrl,
            pwaManifest: row.pwa_manifest || defaultSettings.pwaManifest,
          };
        }
        
        return defaultSettings;
      } catch (error) {
        console.error('Error loading site settings:', error);
        return defaultSettings;
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: (failureCount, error: any) => {
      // Retry up to 2 times for network issues
      return failureCount < 2;
    },
  });
};
